import { useParams, Link, Navigate } from "react-router-dom";
import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, Play, ArrowUpRight, Mail } from "lucide-react";
import { findFilm, FILMS, CONCEPTS } from "../data/films";
import { GALLERY } from "../data/team";
import { Reveal, MaskLines } from "../components/Reveal";

export default function FilmDetail() {
  const { slug } = useParams();
  const film = findFilm(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  if (!film) return <Navigate to="/films" replace />;

  const all = [...FILMS, ...CONCEPTS];
  const currentIdx = all.findIndex((f) => f.slug === slug);
  const next = all[(currentIdx + 1) % all.length];

  return (
    <main data-testid={`film-detail-${slug}`} className="bg-black">
      {/* Hero */}
      <section ref={ref} className="relative min-h-screen overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
          <img src={film.poster} alt={film.title} className="w-full h-[115%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24 min-h-screen flex flex-col justify-between">
          <Link to="/films" data-testid="film-back-link"
            className="text-[11px] uppercase tracking-overline text-white/60 hover:text-white flex items-center gap-2 w-fit">
            <ArrowLeft size={14} /> All Projects
          </Link>

          <div className="mt-auto">
            <span className="inline-block text-[10px] uppercase tracking-overline text-black bg-[color:var(--dk-gold)] px-3 py-1 mb-6">
              {film.status}
            </span>
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">
              {film.genre} · {film.year || "TBA"}
            </p>
            <h1 className="font-serif-display font-light text-white text-[10vw] md:text-[7vw] lg:text-[6rem] leading-[0.95] tracking-tighter">
              <MaskLines lines={[film.title]} delay={0.2} />
            </h1>
            {film.nepaliTitle && (
              <p className="mt-6 font-serif-display italic text-white/60 text-2xl md:text-3xl">
                {film.nepaliTitle} <span className="text-white/30 mx-3">·</span> {film.hindiTitle}
              </p>
            )}
            {film.tagline && (
              <div className="mt-10 flex items-center gap-4 max-w-3xl" data-testid="film-tagline">
                <span className="w-10 h-px bg-[color:var(--dk-gold)] shrink-0" />
                <p className="font-serif-display italic text-[color:var(--dk-gold)] text-xl md:text-2xl lg:text-3xl leading-snug">
                  {film.tagline}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Synopsis + meta */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-28 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        <Reveal className="md:col-span-4">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-4">Production Details</p>
          <dl className="text-sm text-white/70 space-y-3">
            <MetaRow label="Genre" value={film.genre} />
            {film.year && <MetaRow label="Year" value={film.year} />}
            {film.runtime && <MetaRow label="Runtime" value={film.runtime} />}
            {film.language && <MetaRow label="Language" value={film.language} />}
            <MetaRow label="Status" value={film.status} />
            {film.director && <MetaRow label="Director" value={film.director} />}
            {film.writer && <MetaRow label="Writer" value={film.writer} />}
            {film.dop && <MetaRow label="DoP" value={film.dop} />}
            {film.producers && <MetaRow label="Producers" value={film.producers} />}
            {film.location && <MetaRow label="Location" value={film.location} />}
            {film.progress !== undefined && <MetaRow label="Progress" value={`${film.progress}%`} />}
          </dl>
        </Reveal>

        <Reveal delay={1} className="md:col-span-8">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-4">Short Synopsis</p>
          <p className="font-serif-display italic text-white text-3xl md:text-4xl lg:text-5xl leading-[1.15]">
            {film.shortSynopsis}
          </p>

          {film.synopsis && film.synopsis !== film.shortSynopsis && (
            <div className="mt-16">
              <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-4">Extended Synopsis</p>
              <div className="text-white/75 leading-relaxed text-lg max-w-3xl space-y-6">
                {film.synopsis.split("\n\n").map((para, i) => (
                  <p key={i} data-testid={`film-synopsis-para-${i}`}>
                    {i === 0 && para.length > 60 ? (
                      <>
                        <span className="float-left font-serif-display italic text-[color:var(--dk-gold)] text-6xl md:text-7xl leading-none mr-3 mt-1">
                          {para[0]}
                        </span>
                        {para.slice(1)}
                      </>
                    ) : (
                      para
                    )}
                  </p>
                ))}
              </div>
            </div>
          )}
        </Reveal>
      </section>

      {/* Trailer */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Trailer — Placeholder</p>
          <div data-testid="film-trailer-placeholder"
            className="relative aspect-video w-full bg-[color:var(--dk-muted)] border border-white/10 overflow-hidden group">
            <img src={film.poster} alt={`${film.title} trailer`} className="film-hover-img absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 rounded-full border border-[color:var(--dk-gold)] flex items-center justify-center group-hover:bg-[color:var(--dk-red)] transition-colors">
                <Play size={28} className="text-[color:var(--dk-gold)] group-hover:text-white transition-colors fill-current" />
              </div>
            </div>
            <div className="absolute bottom-6 left-6 text-[10px] uppercase tracking-overline text-white/60">
              Trailer coming soon
            </div>
          </div>
        </Reveal>
      </section>

      {/* Cast & Crew */}
      {film.cast && film.cast.length > 0 && (
        <section className="border-t border-white/10 bg-[color:var(--dk-muted)]">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Cast & Crew</p>
            <h2 className="font-serif-display font-light text-white text-4xl md:text-6xl mb-16 tracking-tight">
              Hands on the <span className="italic">work.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {film.cast.map((c, i) => (
                <Reveal key={i} delay={i} className="border-t border-white/15 pt-6">
                  <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)]/70">{String(i + 1).padStart(2, "0")}</p>
                  <p className="font-serif-display text-white text-3xl md:text-4xl mt-2">{c.name}</p>
                  <p className="text-white/60 text-sm mt-1">{c.role}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Festivals */}
      {film.festivals && film.festivals.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Festivals & Market</p>
          {film.festivals.map((f, i) => (
            <Reveal key={i} delay={i} className="border-t border-white/10 py-8 flex items-baseline justify-between gap-6">
              <p className="font-serif-display text-white text-2xl md:text-4xl">{f}</p>
              <span className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] shrink-0">Objective</span>
            </Reveal>
          ))}
        </section>
      )}

      {/* Gallery placeholder */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-6">Stills & Behind-the-scenes</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {GALLERY.slice(0, 4).map((g, i) => (
            <Reveal key={i} delay={i} className="aspect-[3/4] overflow-hidden bg-[color:var(--dk-muted)] group">
              <img src={g.url} alt={g.caption} className="film-hover-img w-full h-full object-cover" />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal className="border border-[color:var(--dk-gold)]/30 bg-[color:var(--dk-muted)] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-3">Production Enquiries</p>
            <h3 className="font-serif-display text-white text-2xl md:text-3xl">Contact the production team for <em className="italic">{film.title}</em></h3>
          </div>
          <Link data-testid="film-contact-cta" to="/contact"
            className="inline-flex items-center gap-3 bg-[color:var(--dk-red)] hover:bg-[color:var(--dk-red-deep)] text-white px-6 py-3 text-[11px] uppercase tracking-overline transition-colors">
            <Mail size={14} /> Contact Team
          </Link>
        </Reveal>
      </section>

      {/* Next film */}
      <section className="border-t border-white/10">
        <Link to={`/films/${next.slug}`} data-testid="film-next-link"
          className="block group max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24 flex items-center justify-between gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-3">Next Project</p>
            <p className="font-serif-display text-white text-3xl md:text-6xl group-hover:text-[color:var(--dk-gold)] transition-colors">
              {next.title}
            </p>
          </div>
          <ArrowUpRight size={40} className="text-white group-hover:text-[color:var(--dk-gold)] group-hover:-translate-y-2 group-hover:translate-x-2 transition-all shrink-0" />
        </Link>
      </section>
    </main>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-white/10 py-2">
      <dt className="text-[10px] uppercase tracking-overline text-white/40">{label}</dt>
      <dd className="text-white/80 text-right">{value}</dd>
    </div>
  );
}
